import { FC } from "react";
import CustomInput from "../../../custom-components/form-inputs/CustomInput";
import { workCreationFirstStepFields } from "../../../validation-schemas/work-schemas/WorkCreationSchema";
import { FormikProps } from "formik";
import { WorkCreationFirstStep } from "../../../domain/models/dto/WorkDTO";
import { useTranslation } from "react-i18next";
import CurrencyIconSVG from "../../resources/svg/CurrencyIconSVG";
import ClockIconSVG from "../../resources/svg/ClockIconSVG";
import WorkCategoriesPopup from "../../resources/components/popups/WorkCategoriesPopup";
import ContactsPeopleIconSVG from "../../resources/svg/ContactsPeopleIconSVG";
import MapIconSVG from "../../resources/svg/MapIconSVG";
import LocationPointIconSVG from "../../resources/svg/LocationPointIcon.SVG";
import LocationIconSVG from "../../resources/svg/LocationIconSVG";
import { WorksCategoriesForm } from "../../../domain/models/dto/WorksCategoriesDTO";
import ClosingCrossLightGrey from "../../resources/svg/functional/ClosingCrossLightGreySVG";
import useScreenSize from "../../../custom-hooks/base-hooks/useScreenSize";
import CreateWorkValidationIcons from "./CreateWorkValidationIcons";
import CustomError from "../../../custom-components/form-inputs/CustomError";
import CustomTextArea from "../../../custom-components/form-inputs/CustomTextArea";
import PriceInput from "../../resources/components/form-inputs/PriceInput";
import useAutocompletedAddress from "../../../custom-hooks/domain/useAutocompletedAddress";
import AutocompleteTooltip from "../../resources/components/AutocompleteTooltip";

interface CreateWorkFirstStepProps {
    form: FormikProps<WorkCreationFirstStep>;
    errorKey: string;
}

const CreateWorkFirstStep: FC<CreateWorkFirstStepProps> = (props) => {
    const { t } = useTranslation();

    const { upTo650, moreOrEqual650, upTo1024, moreOrEqual1024 } = useScreenSize();
    const workCategoriesHandler = (values: WorksCategoriesForm[]) => {
        props.form.setFieldValue("categories", values);
    };
    const unselectCategoryHandler = (value: WorksCategoriesForm) => {
        props.form.setFieldValue(
            "categories",
            props.form.values.categories.filter((item) => item.category !== value.category)
        );
    };
    const onopenWorkCategoriesPopup = () => {
        if (!props.form.touched["categories"]) {
            props.form.setFieldTouched("categories", true);
        }
    };

    const autocompleteHandlers = useAutocompletedAddress(props.form);

    const remainingCategories = props.form.values.categories.length - props.form.values.categories.slice(0, 2)?.length;

    return (
        <div className="relative w-full">
            <div className="relative flex tmax4:flex-col tmin4:items-center w-full mb-6">
                <div className="relative flex flex-col w-full">
                    {upTo650 && (
                        <div className="flex tmax4:mb-3">
                            <p className={`H20B dmax2:H16B text-textWhite tmax4:pt-[2px] tmax4:mr-3`}>
                                {t("workCreation.fields.title")}
                            </p>
                            <CreateWorkValidationIcons names={["title"]} form={props.form} />
                        </div>
                    )}
                    <div className="relative w-full flex items-center gap-5">
                        {moreOrEqual650 && (
                            <p className={`H20B dmax2:H16B text-textWhite tmax4:pt-[2px] tmax4:mr-3`}>
                                {t("workCreation.fields.title")}
                            </p>
                        )}
                        <CustomInput
                            form={props.form}
                            name="title"
                            fields={workCreationFirstStepFields}
                            className="w-full h-[60px] dmax2:h-[45px]"
                        />
                        {moreOrEqual650 && <CreateWorkValidationIcons names={["title"]} form={props.form} />}
                    </div>
                    <CustomError form={props.form} name="title" className="mt-2 dmax2:mt-1" />
                </div>
            </div>
            <div className="relative mb-6">
                <div className="relative flex flex-wrap items-center w-full gap-y-3">
                    <div className="flex tmin4:mr-5">
                        <p className="H20B dmax2:H16B text-textWhite tmax4:pt-[2px] tmax4:mr-3">
                            {t("workCreation.fields.category")}
                        </p>
                        {upTo650 && <CreateWorkValidationIcons names={["categories"]} form={props.form} />}
                    </div>
                    <WorkCategoriesPopup
                        defaultSelected={props.form.values.categories}
                        confirmButtonTitle={t("workCreation.categoryConfirmation")}
                        confirmButtonClassName="w-[315px] dmax2:w-[225px] h-[50px] dmax2:h-[40px] mt-8 dmax2:mt-6"
                        buttonElement={
                            <div className="button dmin2:H16B dmax2:H14B dmin2:w-[225px] dmax2:w-[175px] dmin2:h-[50px] dmax2:h-[40px] mr-5">
                                {t("workCreation.fields.categoryButtonLabel")}
                            </div>
                        }
                        buttonElementClassName="relative"
                        confirmHandler={workCategoriesHandler}
                        onopenPopupHandler={onopenWorkCategoriesPopup}
                    />
                    {props.form.values.categories.slice(0, 2).map((item, index) => (
                        <div key={`chosen-category-${index}`} className="mr-3">
                            <div className="relative flex items-center justify-between border border-textFaded rounded-xl pt-2 dmax2:pt-[6px] pb-[10px] dmax2:pb-2 pl-4 dmax2:pl-3 pr-[40px] dmax2:pr-[34px]">
                                <span className="H16 dmax2:H14 text-textWhite">
                                    {t(`work.categories.${item.category}`)}
                                </span>
                                <ClosingCrossLightGrey
                                    onClick={() => unselectCategoryHandler(item)}
                                    className="absolute right-4 dmax2:right-3 cursor-pointer"
                                />
                            </div>
                        </div>
                    ))}
                    {props.form.values.categories.slice(0, 2)?.length < props.form.values.categories?.length && (
                        <span className="H16B text-textFaded mr-3">
                            {t("workCreation.fields.categoriesOverflowLabel", { value: remainingCategories })}
                        </span>
                    )}
                    {moreOrEqual650 && <CreateWorkValidationIcons names={["categories"]} form={props.form} />}
                </div>
                <CustomError
                    errorKey={
                        !props.form.values.categories?.length &&
                        props.form.touched.categories &&
                        "workCreation.errors.atLeastOneCategory"
                    }
                    className="mt-2 dmax2:mt-1"
                />
            </div>
            <div className="relative flex flex-col tmin4:items-center w-full mb-6">
                <div className="relative flex tmax4:flex-col w-full">
                    {upTo650 && (
                        <div className="flex items-center tmax4:mb-3">
                            <CurrencyIconSVG className="w-8 dmax2:w-6 h-8 dmax2:h-6 mr-3 dmax2:mr-2 text-textWhite" />
                            <p className="H20B dmax2:H16B text-textWhite mr-3">{t("workCreation.fields.price")}</p>
                            <CreateWorkValidationIcons
                                names={["price.amount" as keyof WorkCreationFirstStep]}
                                form={props.form}
                            />
                        </div>
                    )}
                    <div className="flex flex-col">
                        <div className="flex items-center">
                            {moreOrEqual650 && (
                                <>
                                    <CurrencyIconSVG className="w-8 dmax2:w-6 h-8 dmax2:h-6 mr-3 dmax2:mr-2 text-textWhite" />
                                    <p className="H20B dmax2:H16B text-textWhite mr-3">
                                        {t("workCreation.fields.price")}
                                    </p>
                                </>
                            )}
                            <PriceInput
                                form={props.form}
                                name="price"
                                className="w-[120px] tmax4:w-[110px] h-[50px] text-center"
                            />
                            <span className="H20 dmax2:H14B text-textFaded ml-3 dmax2:ml-2">
                                {t("workCreation.fields.priceUnits")}
                            </span>
                            {moreOrEqual650 && (
                                <CreateWorkValidationIcons
                                    names={["price.amount" as keyof WorkCreationFirstStep]}
                                    form={props.form}
                                    wrapClassName="ml-3"
                                />
                            )}
                        </div>
                        <CustomError
                            form={props.form}
                            name={"price.amount" as keyof WorkCreationFirstStep}
                            className="mt-2 dmax2:mt-1"
                        />
                    </div>
                </div>
            </div>
            <div className="relative flex tmax4:flex-col tmin4:items-center w-full mb-6">
                {upTo650 && (
                    <div className="flex items-center tmax4:mb-3">
                        <ClockIconSVG className="w-8 dmax2:w-6 h-8 dmax2:h-6 mr-3 dmax2:mr-2 text-textWhite" />
                        <p className="H20B dmax2:H16B text-textWhite mr-5">
                            {t("workCreation.fields.timeOfExecution")}
                        </p>
                        <CreateWorkValidationIcons names={["estimatedHours"]} form={props.form} />
                    </div>
                )}
                <div className="flex flex-col">
                    <div className="flex items-center">
                        {moreOrEqual650 && (
                            <>
                                <ClockIconSVG className="w-8 dmax2:w-6 h-8 dmax2:h-6 mr-3 dmax2:mr-2 text-textWhite" />
                                <p className="H20B dmax2:H16B text-textWhite mr-5">
                                    {t("workCreation.fields.timeOfExecution")}
                                </p>
                            </>
                        )}
                        <CustomInput
                            form={props.form}
                            name="estimatedHours"
                            fields={workCreationFirstStepFields}
                            className="w-[100px] tmax4:w-[110px] h-[50px] text-center"
                        />
                        <span className="H20 dmax2:H14B text-textFaded ml-3 dmax2:ml-2">
                            {t("workCreation.fields.timeOfExecutionUnits")}
                        </span>
                        {moreOrEqual650 && (
                            <CreateWorkValidationIcons
                                names={["estimatedHours"]}
                                form={props.form}
                                wrapClassName="ml-3"
                            />
                        )}
                    </div>
                    <CustomError name="estimatedHours" form={props.form} className="mt-2 dmax2:mt-1" />
                </div>
            </div>
            <div className="relative flex tmax4:flex-col tmin4:items-center w-full mb-6">
                {upTo650 && (
                    <div className="flex items-center tmax4:mb-3">
                        <ContactsPeopleIconSVG className="w-8 dmax2:w-6 h-8 dmax2:h-6 mr-3 dmax2:mr-2 text-textWhite" />
                        <p className="H20B dmax2:H16B text-textWhite mr-5">
                            {t("workCreation.fields.numberOfWorkers")}
                        </p>
                        <CreateWorkValidationIcons names={["workersAmount"]} form={props.form} />
                    </div>
                )}
                <div className="flex flex-col">
                    <div className="flex items-center">
                        {moreOrEqual650 && (
                            <>
                                <ContactsPeopleIconSVG className="w-8 dmax2:w-6 h-8 dmax2:h-6 mr-3 dmax2:mr-2 text-textWhite" />
                                <p className="H20B dmax2:H16B text-textWhite mr-5">
                                    {t("workCreation.fields.numberOfWorkers")}
                                </p>
                            </>
                        )}
                        <CustomInput
                            form={props.form}
                            name="workersAmount"
                            fields={workCreationFirstStepFields}
                            className="w-[100px] tmax4:w-[110px] h-[50px] text-center"
                        />
                        {moreOrEqual650 && (
                            <CreateWorkValidationIcons
                                names={["workersAmount"]}
                                form={props.form}
                                wrapClassName="ml-3"
                            />
                        )}
                    </div>
                    <CustomError name="workersAmount" form={props.form} className="mt-2 dmax2:mt-1" />
                </div>
            </div>
            <div className="flex flex-col w-full justify-between dmin2:mb-6 td4:mb-4 tmax4:mb-2">
                <div className="flex items-center mb-4 tmax4:mb-3">
                    <p className="H20B dmax2:H16B text-textWhite mr-5">{t("workCreation.fields.workLocation")}</p>
                    <CreateWorkValidationIcons
                        names={[
                            "address.country" as keyof WorkCreationFirstStep,
                            "address.city" as keyof WorkCreationFirstStep,
                            "address.address" as keyof WorkCreationFirstStep,
                        ]}
                        form={props.form}
                    />
                </div>
                <div className="relative flex w-full">
                    <div className="relative w-full dmin2:mr-6 td4:mr-4 tmax4:mr-2">
                        <MapIconSVG className="w-8 dmax2:w-6 h-8 dmax2:h-6 absolute left-6 top-[14px] dmax2:top-[10px] text-textFaded" />
                        <CustomInput
                            form={props.form}
                            name="address"
                            nestedKey="country"
                            fields={workCreationFirstStepFields.address.schema}
                            className="w-full h-[60px] dmax2:h-[45px] pl-[70px]"
                            onChange={autocompleteHandlers.autocompleteAddress("country")}
                        />
                        <CustomError name="address" nestedKey="country" form={props.form} className="mt-2 dmax2:mt-1" />
                        <AutocompleteTooltip
                            data={autocompleteHandlers.countries}
                            resetCallback={autocompleteHandlers.resetCountries}
                            selectCallback={autocompleteHandlers.selectCountry}
                            className={`px-[${upTo1024 ? "20" : "46"}px]`}
                        />
                    </div>
                    <div className="relative w-full dmin2:mr-6 di6:mr-4">
                        <LocationPointIconSVG className="w-8 dmax2:w-6 h-8 dmax2:h-6 absolute left-6 top-[14px] dmax2:top-[10px] text-textFaded" />
                        <CustomInput
                            form={props.form}
                            name="address"
                            nestedKey="city"
                            fields={workCreationFirstStepFields.address.schema}
                            className="w-full h-[60px] dmax2:h-[45px] pl-[70px] mr-6"
                            onChange={autocompleteHandlers.autocompleteAddress("city")}
                        />
                        <CustomError name="address" nestedKey="city" form={props.form} className="mt-2 dmax2:mt-1" />
                        <AutocompleteTooltip
                            data={autocompleteHandlers.cities}
                            resetCallback={autocompleteHandlers.resetCities}
                            selectCallback={autocompleteHandlers.selectCity}
                            className={`px-[${upTo1024 ? "20" : "46"}px]`}
                        />
                    </div>
                    {moreOrEqual1024 && (
                        <div className="relative w-full">
                            <LocationIconSVG className="w-8 dmax2:w-6 h-8 dmax2:h-6 absolute left-6 top-[14px] dmax2:top-[10px] text-textFaded" />
                            <CustomInput
                                form={props.form}
                                name="address"
                                nestedKey="address"
                                fields={workCreationFirstStepFields.address.schema}
                                className="w-full h-[60px] dmax2:h-[45px] pl-[70px]"
                                onChange={autocompleteHandlers.autocompleteAddress("address")}
                            />
                            <CustomError
                                name="address"
                                nestedKey="address"
                                form={props.form}
                                className="mt-2 dmax2:mt-1"
                            />
                            <AutocompleteTooltip
                                data={autocompleteHandlers.addresses}
                                resetCallback={autocompleteHandlers.resetAddresses}
                                selectCallback={autocompleteHandlers.selectAddress}
                                className="px-[46px]"
                            />
                        </div>
                    )}
                </div>
            </div>
            {upTo1024 && (
                <div className="relative w-full">
                    <LocationIconSVG className="w-8 dmax2:w-6 h-8 dmax2:h-6 absolute left-6 top-[14px] dmax2:top-[10px] text-textFaded" />
                    <CustomInput
                        form={props.form}
                        name="address"
                        nestedKey="address"
                        fields={workCreationFirstStepFields.address.schema}
                        className="w-full h-[60px] dmax2:h-[45px] pl-[70px]"
                        onChange={autocompleteHandlers.autocompleteAddress("address")}
                    />
                    <CustomError name="address" nestedKey="address" form={props.form} className="mt-2 dmax2:mt-1" />
                    <AutocompleteTooltip
                        data={autocompleteHandlers.addresses}
                        resetCallback={autocompleteHandlers.resetAddresses}
                        selectCallback={autocompleteHandlers.selectAddress}
                        className="px-[46px]"
                    />
                </div>
            )}
            <div className="relative w-full mt-9 dmax2:mt-6">
                <div className="flex items-center mb-6 dmax2:mb-3">
                    <p className="H20B dmax2:H16B text-textWhite">{t("workCreation.fields.jobDescription")}</p>
                    <CreateWorkValidationIcons names={["description"]} form={props.form} wrapClassName="ml-3" />
                </div>
                <CustomTextArea
                    form={props.form}
                    name="description"
                    fields={workCreationFirstStepFields}
                    charactersRestrictionKey="common.characters"
                />
                <CustomError form={props.form} name="description" className="mt-2 dmax2:mt-1" />
            </div>
            <CustomError className="mt-2 dmax2:mt-1" errorKey={props.errorKey} />
            <div
                onClick={() => props.form.handleSubmit()}
                className="button H20 dmax2:H14B w-[193px] dmax2:w-[175px] h-[50px] dmax2:h-[40px] mt-9 dmax2:mt-6"
            >
                <div className="mb-[2px] dmax2:mb-[1px]">{t("common.next")}</div>
            </div>
        </div>
    );
};

export default CreateWorkFirstStep;
